import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';
import { buttonVariants } from './KoobButton';
import { KoobMenu } from './KoobMenu';
import KoobLoader from './KoobLoader';

type KoobButtonMenuProps = VariantProps<typeof buttonVariants> & {
  /** Trigger label. */
  label?: string;
  /** Trigger icon class. */
  icon?: string;
  /** Hides the label text, keeping it as the accessible name. */
  iconOnly?: boolean;
  /** Shows a chevron after the label, for a dropdown of choices. */
  caret?: boolean;
  /** KoobMenuItem elements. */
  menu: React.ReactNode;
  /** Swaps the icon for a spinner and disables the trigger. */
  loading?: boolean;
  disabled?: boolean;
  testId?: string;
};

/** "More actions" button opening a Koob dropdown menu. */
const KoobButtonMenu = ({
  variant = 'secondary',
  size,
  label = 'More actions',
  icon = 'fa-regular fa-ellipsis',
  iconOnly = false,
  caret = false,
  menu,
  loading = false,
  disabled,
  testId,
}: KoobButtonMenuProps) => (
  <PopoverPrimitive.Root>
    <PopoverPrimitive.Trigger asChild>
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-testid={testId}
        className={cn(buttonVariants({ variant, size }), iconOnly && 'px-3')}
      >
        {loading ? (
          <KoobLoader color="currentColor" size={16} />
        ) : (
          <i className={icon} aria-hidden="true" />
        )}
        {!iconOnly && label}
        {caret && (
          <i
            className="fa-solid fa-chevron-down text-[10px]"
            aria-hidden="true"
          />
        )}
      </button>
    </PopoverPrimitive.Trigger>
    <KoobMenu>{menu}</KoobMenu>
  </PopoverPrimitive.Root>
);

export default KoobButtonMenu;
